// System Health tab — operational incidents surfaced for triage.
//
// Backed by the system_events table. The whole point is: an operator
// sees WHAT broke, for WHICH account, and a "where to look" hint —
// without SSHing in blind. Account search is the support workflow
// ("customer X reports a problem" → filter by their email/org).

import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Loading } from "@/components/ui/Loading";
import { SelectMenu } from "@/components/ui/SelectMenu";
import { toast } from "@/stores/toastStore";
import {
  useResolveSystemEvent,
  useSystemEvents,
  type SystemEventsFilters,
} from "@/hooks/useAdmin";
import type { SystemEvent } from "@/types/admin";
import { useDict } from "@/i18n/LocaleProvider";

const LIMIT = 50;

const SEV_COLOR: Record<string, { fg: string; bg: string }> = {
  critical: { fg: "rgb(var(--color-danger))", bg: "rgb(var(--color-danger) / 0.13)" },
  error: { fg: "rgb(var(--color-warning))", bg: "rgb(var(--color-warning) / 0.13)" },
  warning: { fg: "rgb(var(--color-warning))", bg: "rgb(var(--color-warning) / 0.13)" },
};

function fmt(ts: string): string {
  return new Date(ts).toLocaleString();
}

function EventRow({ ev }: { ev: SystemEvent }) {
  const t = useDict().app.adminHealth;
  const [open, setOpen] = useState(false);
  const resolve = useResolveSystemEvent();
  const sev = SEV_COLOR[ev.severity] ?? SEV_COLOR.error;
  const hint =
    ev.context && typeof ev.context["where_to_look"] === "string"
      ? (ev.context["where_to_look"] as string)
      : null;

  return (
    <Card className="!p-3 mb-2">
      <div className="flex items-start gap-3 flex-wrap">
        <span
          className="px-2 py-[2px] rounded-[3px] font-mono text-[9px] font-bold uppercase tracking-[0.08em] shrink-0"
          style={{ color: sev.fg, background: sev.bg, border: `1px solid ${sev.fg}66` }}
        >
          {t.sevLabels[ev.severity as keyof typeof t.sevLabels] ?? ev.severity}
        </span>
        <span className="font-mono text-[10px] text-accent uppercase tracking-[0.06em] shrink-0">
          {t.kindLabels[ev.kind as keyof typeof t.kindLabels] ?? ev.kind}
        </span>
        <span className="font-sans text-[13px] text-content flex-1 min-w-[200px]">
          {ev.summary}
        </span>
        {ev.resolved_at ? (
          <span className="font-mono text-[10px] text-success shrink-0">
            {t.resolved}
          </span>
        ) : (
          <Button
            variant="default"
            small
            disabled={resolve.isPending}
            onClick={() =>
              resolve.mutate(ev.id, {
                onSuccess: () => toast.success(t.incidentResolved),
                onError: (e) =>
                  toast.error(e instanceof Error ? e.message : t.failed),
              })
            }
          >
            {t.resolve}
          </Button>
        )}
      </div>

      <div className="flex items-center gap-4 mt-2 font-mono text-[10px] text-muted flex-wrap">
        <span>{fmt(ev.created_at)}</span>
        {ev.organization_name && (
          <span className="text-content">🏢 {ev.organization_name}</span>
        )}
        {ev.user_email && (
          <span className="text-content">✉ {ev.user_email}</span>
        )}
        {ev.context && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="bg-transparent border-none text-accent underline cursor-pointer p-0 font-mono text-[10px]"
          >
            {open ? t.hide : t.detail}
          </button>
        )}
      </div>

      {open && ev.context && (
        <div className="mt-2 p-2 bg-bg2 rounded border border-stroke">
          {hint && (
            <div className="font-mono text-[11px] text-accent mb-2 leading-[1.5]">
              {t.whereToLook} {hint}
            </div>
          )}
          <pre className="font-mono text-[10px] text-muted whitespace-pre-wrap break-words m-0">
            {JSON.stringify(ev.context, null, 2)}
          </pre>
        </div>
      )}
    </Card>
  );
}

export function AdminHealthTab() {
  const t = useDict().app.adminHealth;
  const [severity, setSeverity] = useState("");
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState<SystemEventsFilters["status"]>("open");
  const [account, setAccount] = useState("");
  const [offset, setOffset] = useState(0);

  const q = useSystemEvents({
    severity: severity || undefined,
    kind: kind || undefined,
    account: account || undefined,
    status,
    limit: LIMIT,
    offset,
  });

  const events = q.data?.events ?? [];
  const total = q.data?.total ?? 0;

  return (
    <div>
      <div className="font-mono text-[11px] text-muted leading-[1.6] mb-4">
        {t.intro}
      </div>

      <div className="flex gap-3 mb-4 flex-wrap items-end">
        <div className="w-[180px]">
          <Input
            label={t.accountSearch}
            placeholder={t.accountPlaceholder}
            value={account}
            onChange={(v) => {
              setAccount(v);
              setOffset(0);
            }}
          />
        </div>
        <div className="w-[150px]">
          <div className="font-mono text-[10px] text-muted uppercase tracking-[0.08em] mb-1">
            {t.status}
          </div>
          <SelectMenu<SystemEventsFilters["status"]>
            value={status}
            options={[
              { value: "open", label: t.statusOpen },
              { value: "resolved", label: t.statusResolved },
              { value: "all", label: t.statusAll },
            ]}
            onChange={(v) => {
              setStatus(v);
              setOffset(0);
            }}
            ariaLabel={t.statusAria}
          />
        </div>
        <div className="w-[150px]">
          <div className="font-mono text-[10px] text-muted uppercase tracking-[0.08em] mb-1">
            {t.severity}
          </div>
          <SelectMenu<string>
            value={severity}
            options={[
              { value: "", label: t.sevAny },
              { value: "critical", label: t.sevCritical },
              { value: "error", label: t.sevError },
              { value: "warning", label: t.sevWarning },
            ]}
            onChange={(v) => {
              setSeverity(v);
              setOffset(0);
            }}
            ariaLabel={t.severityAria}
          />
        </div>
        <div className="w-[200px]">
          <div className="font-mono text-[10px] text-muted uppercase tracking-[0.08em] mb-1">
            {t.kind}
          </div>
          <SelectMenu<string>
            value={kind}
            options={[
              { value: "", label: t.kindAny },
              { value: "scan_timeout", label: t.kindScanTimeout },
              { value: "resend_failure", label: t.kindEmailFailure },
              { value: "ai_enrichment_failure", label: t.kindAiFailure },
            ]}
            onChange={(v) => {
              setKind(v);
              setOffset(0);
            }}
            ariaLabel={t.kindAria}
          />
        </div>
      </div>

      {q.isLoading ? (
        <Loading text={t.loading} />
      ) : events.length === 0 ? (
        <Card className="!p-6 text-center">
          <div className="text-3xl mb-2">✓</div>
          <div className="font-mono text-[12px] text-muted">
            {t.noIncidents} {status === "open" && t.nothingOnFire}
          </div>
        </Card>
      ) : (
        <>
          {events.map((ev) => (
            <EventRow key={ev.id} ev={ev} />
          ))}
          <div className="flex items-center justify-between mt-3 font-mono text-[11px] text-muted">
            <span>
              {offset + 1}–{Math.min(offset + LIMIT, total)} {t.rangeOf} {total}
            </span>
            <div className="flex gap-2">
              <Button
                variant="default"
                small
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - LIMIT))}
              >
                {t.prev}
              </Button>
              <Button
                variant="default"
                small
                disabled={offset + LIMIT >= total}
                onClick={() => setOffset(offset + LIMIT)}
              >
                {t.next}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
